import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import antivirusService from "../../services/antivirusService";

const card = {
  background: "var(--vz-secondary-bg, #fff)",
  border: "1px solid var(--vz-border-color)",
  borderRadius: 10,
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  overflow: "hidden",
};

const inputStyle = {
  background: "var(--vz-input-bg, var(--vz-secondary-bg, #fff))",
  border: "1px solid var(--vz-border-color)",
  color: "var(--vz-body-color)",
  padding: "8px 12px", borderRadius: 8, fontSize: 13, width: "100%", outline: "none"
};

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const MONTHS_DAYS = Array.from({ length: 28 }, (_, i) => i + 1);

const ScheduledScans = () => {
  const [scans,      setScans]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showModal,  setShowModal]  = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alert,      setAlert]      = useState({ show: false, message: "", type: "info" });

  const [form, setForm] = useState({
    name: "", scan_type: "quickscan", schedule_type: "daily",
    schedule_time: "02:00", weekly_day: "monday", monthly_day: 1, enabled: true
  });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await antivirusService.getSchedules();
      setScans(res.schedules || res.scheduled_scans || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const showMsg = (message, type = "info") => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: "", type: "info" }), 4000);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await antivirusService.createSchedule(form);
      showMsg("Schedule created successfully", "success");
      setShowModal(false);
      setForm({ name: "", scan_type: "quickscan", schedule_type: "daily", schedule_time: "02:00", weekly_day: "monday", monthly_day: 1, enabled: true });
      load();
    } catch {
      showMsg("Failed to create schedule", "danger");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this schedule?")) return;
    try {
      await antivirusService.deleteSchedule(id);
      showMsg("Schedule deleted", "success");
      load();
    } catch {
      showMsg("Delete failed", "danger");
    }
  };

  const handleToggle = async (scan) => {
    try {
      await antivirusService.updateSchedule(scan.id, { enabled: !scan.enabled });
      load();
    } catch {
      showMsg("Failed to update schedule", "danger");
    }
  };

  const formatSchedule = (scan) => {
    if (scan.schedule_type === "daily")    return `Daily at ${scan.schedule_time}`;
    if (scan.schedule_type === "weekly")   return `Every ${scan.weekly_day} at ${scan.schedule_time}`;
    if (scan.schedule_type === "monthly")  return `Monthly on day ${scan.monthly_day} at ${scan.schedule_time}`;
    if (scan.schedule_type === "interval") return `Every ${scan.interval_value} ${scan.interval_unit}`;
    return scan.schedule_type;
  };

  const scanTypeMeta = (type) => {
    const isQuick = (type || "").includes("quick");
    return {
      color:  isQuick ? "var(--vz-info)"    : "var(--vz-warning)",
      bg:     isQuick ? "rgba(var(--vz-info-rgb),.1)"    : "rgba(var(--vz-warning-rgb),.1)",
      border: isQuick ? "rgba(var(--vz-info-rgb),.3)"    : "rgba(var(--vz-warning-rgb),.3)",
      icon:   isQuick ? "zap"               : "shield",
      label:  isQuick ? "Quick Scan"        : "Full Scan"
    };
  };

  return (
    <>
      {/* Alert toast */}
      {alert.show && (
        <div style={{
          position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)",
          zIndex: 9999, padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600,
          background: alert.type === "success" ? "var(--vz-success)" : alert.type === "danger" ? "var(--vz-danger)" : "var(--vz-info)",
          color: "#fff", boxShadow: "0 4px 16px rgba(0,0,0,.15)"
        }}>
          {alert.message}
        </div>
      )}

      <div style={card}>
        {/* Header */}
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--vz-border-color)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: "var(--vz-body-color)" }}>
                <FeatherIcon icon="calendar" size={15} style={{ color: "var(--vz-success)", marginRight: 6, verticalAlign: "middle" }} />
                Scheduled Scans
              </span>
              <span style={{ fontSize: 12, padding: "2px 10px", background: "rgba(var(--vz-primary-rgb),.1)", borderRadius: 20, color: "var(--vz-primary)", fontWeight: 600 }}>
                {scans.length}
              </span>
            </div>
            <p style={{ color: "var(--vz-secondary-color)", fontSize: 12, margin: 0 }}>Automated scan tasks across all endpoints</p>
          </div>
          <button onClick={() => setShowModal(true)} style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "7px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
            background: "var(--vz-success)", border: "none", color: "#fff", cursor: "pointer",
            boxShadow: "0 2px 8px rgba(var(--vz-success-rgb),.3)"
          }}>
            <FeatherIcon icon="plus" size={14} />New Schedule
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ padding: "48px 24px", textAlign: "center" }}>
            <Spinner color="primary" size="sm" />
            <p style={{ marginTop: 8, color: "var(--vz-secondary-color)", fontSize: 13 }}>Loading schedules...</p>
          </div>
        ) : scans.length === 0 ? (
          <div style={{ padding: "48px 24px", textAlign: "center", color: "var(--vz-secondary-color)" }}>
            <FeatherIcon icon="calendar" size={36} style={{ opacity: 0.25, display: "block", margin: "0 auto 12px" }} />
            <p style={{ fontSize: 14, fontWeight: 600, margin: "0 0 4px", color: "var(--vz-body-color)" }}>No scheduled scans configured</p>
            <p style={{ fontSize: 13, margin: "0 0 16px" }}>Set up automated scans to run on a schedule</p>
            <button onClick={() => setShowModal(true)} style={{
              padding: "8px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600,
              background: "transparent", border: "1px solid var(--vz-success)",
              color: "var(--vz-success)", cursor: "pointer"
            }}>
              Create First Schedule
            </button>
          </div>
        ) : (
          <>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {["Task Name", "Scan Type", "Schedule", "Next Run", "Last Run", "Total Runs", "Status", "Actions"].map((h, i) => (
                      <th key={i} style={{
                        padding: "10px 18px", fontSize: 11, fontWeight: 700,
                        textTransform: "uppercase", letterSpacing: ".06em",
                        color: "var(--vz-secondary-color)", textAlign: "left",
                        borderBottom: "1px solid var(--vz-border-color)",
                        background: "var(--vz-light, rgba(0,0,0,.02))", whiteSpace: "nowrap"
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {scans.map((scan) => {
                    const tb = scanTypeMeta(scan.scan_type);
                    return (
                      <tr key={scan.id}
                        style={{ borderBottom: "1px solid var(--vz-border-color)" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(var(--vz-primary-rgb),.03)"}
                        onMouseLeave={e => e.currentTarget.style.background = ""}
                      >
                        <td style={{ padding: "12px 18px" }}>
                          <span style={{ fontWeight: 600, fontSize: 13, color: "var(--vz-body-color)" }}>{scan.name}</span>
                          <span style={{ display: "block", fontSize: 11, color: "var(--vz-secondary-color)" }}>
                            <FeatherIcon icon="monitor" size={10} style={{ marginRight: 4 }} />All Agents
                          </span>
                        </td>
                        <td style={{ padding: "12px 18px" }}>
                          <span style={{
                            display: "inline-flex", alignItems: "center", gap: 4,
                            padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                            color: tb.color, background: tb.bg, border: `1px solid ${tb.border}`
                          }}>
                            <FeatherIcon icon={tb.icon} size={11} />{tb.label}
                          </span>
                        </td>
                        <td style={{ padding: "12px 18px", fontSize: 12, color: "var(--vz-secondary-color)" }}>
                          <FeatherIcon icon="clock" size={11} style={{ marginRight: 4, color: "var(--vz-info)" }} />
                          {formatSchedule(scan)}
                        </td>
                        <td style={{ padding: "12px 18px", fontSize: 12, color: "var(--vz-secondary-color)" }}>
                          {scan.next_run
                            ? new Date(scan.next_run).toLocaleString([], { dateStyle: "short", timeStyle: "short" })
                            : "—"}
                        </td>
                        <td style={{ padding: "12px 18px", fontSize: 12, color: "var(--vz-secondary-color)" }}>
                          {scan.last_run
                            ? new Date(scan.last_run).toLocaleString([], { dateStyle: "short", timeStyle: "short" })
                            : "Never"}
                        </td>
                        <td style={{ padding: "12px 18px" }}>
                          <span style={{
                            padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
                            background: "rgba(var(--vz-primary-rgb),.08)", color: "var(--vz-primary)"
                          }}>
                            {scan.total_runs || 0}
                          </span>
                        </td>
                        <td style={{ padding: "12px 18px" }}>
                          <div className="form-check form-switch mb-0">
                            <input
                              className="form-check-input" type="checkbox"
                              checked={scan.enabled} onChange={() => handleToggle(scan)}
                              style={{ cursor: "pointer" }}
                            />
                          </div>
                        </td>
                        <td style={{ padding: "12px 18px" }}>
                          <button onClick={() => handleDelete(scan.id)} style={{
                            display: "inline-flex", alignItems: "center", gap: 4,
                            padding: "4px 10px", borderRadius: 6, fontSize: 12, cursor: "pointer",
                            background: "rgba(var(--vz-danger-rgb),.1)",
                            border: "1px solid rgba(var(--vz-danger-rgb),.3)",
                            color: "var(--vz-danger)"
                          }}>
                            <FeatherIcon icon="trash-2" size={12} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ padding: "10px 18px", borderTop: "1px solid var(--vz-border-color)", background: "rgba(var(--vz-primary-rgb),.02)" }}>
              <span style={{ fontSize: 12, color: "var(--vz-secondary-color)" }}>
                <strong style={{ color: "var(--vz-primary)" }}>{scans.length}</strong> schedule{scans.length !== 1 ? "s" : ""} configured
              </span>
            </div>
          </>
        )}
      </div>

      {/* ── CREATE SCHEDULE MODAL ── */}
      <Modal isOpen={showModal} toggle={() => setShowModal(false)} centered size="lg">
        <div style={{
          background: "var(--vz-secondary-bg, #fff)",
          border: "1px solid var(--vz-border-color)",
          borderRadius: 12, overflow: "hidden"
        }}>
          <Form onSubmit={handleSubmit}>
            <ModalHeader toggle={() => setShowModal(false)}
              style={{ background: "var(--vz-light, rgba(0,0,0,.02))", borderBottom: "1px solid var(--vz-border-color)", color: "var(--vz-body-color)" }}>
              <FeatherIcon icon="calendar" size={15} style={{ color: "var(--vz-success)", marginRight: 8, verticalAlign: "middle" }} />
              New Scheduled Scan
            </ModalHeader>

            <ModalBody style={{ padding: "20px 24px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16, marginBottom: 16 }}>
                <FormGroup className="mb-0">
                  <Label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--vz-secondary-color)", marginBottom: 6 }}>
                    Task Name
                  </Label>
                  <input name="name" style={inputStyle} placeholder="e.g., Nightly Security Sweep" onChange={handleChange} required />
                </FormGroup>
                <FormGroup className="mb-0">
                  <Label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--vz-secondary-color)", marginBottom: 6 }}>
                    Scan Type
                  </Label>
                  <select name="scan_type" style={inputStyle} onChange={handleChange}>
                    <option value="quickscan">Quick Scan</option>
                    <option value="fullscan">Full Scan</option>
                  </select>
                </FormGroup>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                <FormGroup className="mb-0">
                  <Label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--vz-secondary-color)", marginBottom: 6 }}>
                    Frequency
                  </Label>
                  <select name="schedule_type" style={inputStyle} value={form.schedule_type} onChange={handleChange}>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="interval">Custom Interval</option>
                  </select>
                </FormGroup>
                <FormGroup className="mb-0">
                  <Label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--vz-secondary-color)", marginBottom: 6 }}>
                    Time
                  </Label>
                  <input type="time" name="schedule_time" style={inputStyle} value={form.schedule_time} onChange={handleChange} required />
                </FormGroup>
              </div>

              {form.schedule_type === "weekly" && (
                <FormGroup className="mb-4">
                  <Label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--vz-secondary-color)", marginBottom: 6 }}>
                    Day of Week
                  </Label>
                  <select name="weekly_day" style={inputStyle} value={form.weekly_day} onChange={handleChange}>
                    {DAYS.map(d => <option key={d} value={d.toLowerCase()}>{d}</option>)}
                  </select>
                </FormGroup>
              )}

              {form.schedule_type === "monthly" && (
                <FormGroup className="mb-4">
                  <Label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--vz-secondary-color)", marginBottom: 6 }}>
                    Day of Month
                  </Label>
                  <select name="monthly_day" style={inputStyle} value={form.monthly_day} onChange={handleChange}>
                    {MONTHS_DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                    <option value={-1}>Last day of month</option>
                  </select>
                </FormGroup>
              )}

              {form.schedule_type === "interval" && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                  <FormGroup className="mb-0">
                    <Label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--vz-secondary-color)", marginBottom: 6 }}>Every</Label>
                    <input type="number" name="interval_value" min={1} style={inputStyle} placeholder="6" onChange={handleChange} required />
                  </FormGroup>
                  <FormGroup className="mb-0">
                    <Label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--vz-secondary-color)", marginBottom: 6 }}>Unit</Label>
                    <select name="interval_unit" style={inputStyle} onChange={handleChange}>
                      <option value="hours">Hours</option>
                      <option value="minutes">Minutes</option>
                    </select>
                  </FormGroup>
                </div>
              )}

              {/* Preview */}
              <div style={{
                padding: "12px 16px", borderRadius: 8,
                background: "rgba(var(--vz-success-rgb),.05)",
                border: "1px solid rgba(var(--vz-success-rgb),.2)"
              }}>
                <p style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 700, color: "var(--vz-secondary-color)", margin: "0 0 4px" }}>Preview</p>
                <p style={{ fontSize: 13, color: "var(--vz-body-color)", margin: 0, display: "flex", alignItems: "center", gap: 6 }}>
                  <FeatherIcon icon="info" size={13} style={{ color: "var(--vz-info)" }} />
                  {form.schedule_type === "daily"    && `Runs every day at ${form.schedule_time}`}
                  {form.schedule_type === "weekly"   && `Runs every ${form.weekly_day} at ${form.schedule_time}`}
                  {form.schedule_type === "monthly"  && `Runs on day ${form.monthly_day === -1 ? "last" : form.monthly_day} of every month at ${form.schedule_time}`}
                  {form.schedule_type === "interval" && `Runs every ${form.interval_value || "?"} ${form.interval_unit || "hours"}`}
                </p>
              </div>
            </ModalBody>

            <ModalFooter style={{ background: "var(--vz-light, rgba(0,0,0,.02))", borderTop: "1px solid var(--vz-border-color)", padding: "12px 20px" }}>
              <button type="button" onClick={() => setShowModal(false)} style={{
                background: "none", border: "1px solid var(--vz-border-color)", borderRadius: 8,
                padding: "7px 18px", fontSize: 13, fontWeight: 600,
                color: "var(--vz-secondary-color)", cursor: "pointer"
              }}>
                Cancel
              </button>
              <button type="submit" disabled={submitting} style={{
                background: "var(--vz-success)", border: "none", color: "#fff",
                borderRadius: 8, padding: "7px 24px", fontWeight: 600,
                fontSize: 13, cursor: "pointer", opacity: submitting ? 0.7 : 1,
                display: "inline-flex", alignItems: "center", gap: 6
              }}>
                {submitting ? <><Spinner size="sm" />Creating...</> : "Activate Schedule"}
              </button>
            </ModalFooter>
          </Form>
        </div>
      </Modal>
    </>
  );
};

export default ScheduledScans;
